import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../MainLayout';
import { PageHeading } from '../../layout/PageHeading';
import { PianoIdentity } from '../../layout/PianoIdentity';
import { PrintButton } from '../../layout/PrintButton';
import { AnalysisFooter } from '@/components/analyze/key-reading/AnalysisFooter';
import { KeyReadingStrip } from '@/components/analyze/key-reading/KeyReadingStrip';
import { SupportSpringBalanceWeightChart } from '@/components/charts/SupportSpringBalanceWeightChart';
import { serieValueAt, type ChartReadingContext } from '@/components/charts/interfaces';
import { Button } from '@/components/ui/button';
import { useAnalyzedKeyboard } from '@/hooks/keyboard/use-analyzed-keyboard';
import { useMeasureOptions, useMeasureOptionsActions } from '@/hooks/store/use-measure-options-store';
import { useTranslation } from '@/hooks/use-translation';
import { formatGrams } from '@/lib/format/format-weight';
import { usePrintLandscape } from '@/hooks/use-print-page-size';
const DESIGN_PATH = '/design/wippen-support-springs';
export const AnalyzeWippenSupportSpringPage = () => {
  const $ = _c(43);
  usePrintLandscape();
  const analyzedKeyboard = useAnalyzedKeyboard();
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    updateOption
  } = useMeasureOptionsActions();
  const {
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const navigate = useNavigate();
  let t0;
  if ($[0] !== analyzedKeyboard || $[1] !== useSupportSpringMeasurements) {
    t0 = useSupportSpringMeasurements && analyzedKeyboard.getKeys().some(_temp);
    $[0] = analyzedKeyboard;
    $[1] = useSupportSpringMeasurements;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hasReadings = t0;
  let t1;
  if ($[3] !== navigate || $[4] !== updateOption) {
    t1 = () => {
      updateOption("useSupportSpringMeasurements", true);
      navigate("/measure");
    };
    $[3] = navigate;
    $[4] = updateOption;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const enableMeasurement = t1;
  let t2;
  if ($[6] !== analyzedKeyboard || $[7] !== currentLanguage || $[8] !== t) {
    t2 = t3 => {
      const {
        hoveredKey,
        measuredSerie
      } = t3;
      return <KeyReadingStrip reading={{
        keyNumber: hoveredKey,
        noteName: analyzedKeyboard.getKeyByNumber(hoveredKey ?? 0)?.name ?? null,
        rows: [{
          isAccent: true,
          label: t("Measured spring-supported weight"),
          value: formatGrams(serieValueAt(measuredSerie, hoveredKey), currentLanguage)
        }]
      }} />;
    };
    $[6] = analyzedKeyboard;
    $[7] = currentLanguage;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const renderReading = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PianoIdentity />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hasReadings) {
    t4 = hasReadings ? <PrintButton /> : null;
    $[11] = hasReadings;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <em className="ptl-accent">Springs</em>;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = <PageHeading title={<Trans t={t} i18nKey="Wippen Support <1>Springs</1>">Wippen Support {t6}</Trans>} />;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("Enable the option");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== enableMeasurement || $[21] !== t8) {
    t9 = <Button className="min-h-[46px]" onClick={enableMeasurement}>{t8}</Button>;
    $[20] = enableMeasurement;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("Design the springs");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArrowRight />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10) {
    t12 = <Button asChild={true} variant="outlineStrong" className="min-h-[46px]"><Link to={DESIGN_PATH}>{t10}{t11}</Link></Button>;
    $[26] = t10;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t9) {
    t13 = <>{t9}{t12}</>;
    $[28] = t12;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== analyzedKeyboard || $[32] !== renderReading || $[33] !== t13) {
    t14 = <SupportSpringBalanceWeightChart keyboard={analyzedKeyboard} renderReading={renderReading} emptyStateActions={t13} />;
    $[31] = analyzedKeyboard;
    $[32] = renderReading;
    $[33] = t13;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== hasReadings || $[36] !== t) {
    t15 = hasReadings ? <AnalysisFooter action={{
      label: t("Design the springs"),
      to: DESIGN_PATH
    }} /> : null;
    $[35] = hasReadings;
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15 || $[40] !== t5 || $[41] !== t7) {
    t16 = <MainLayout headerRight={t5}>{t7}{t14}{t15}</MainLayout>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t5;
    $[41] = t7;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
};
function _temp(key) {
  return key.payload.supportSpringBalanceWeight !== null;
}